import { asyncRouterMap, constantRouterMap, resetRouter } from '@/router/index';
import { formatPointText } from '@/utils/pointText';

/**
 * 兼容仅配置了数据字典的后端菜单，让原有字典权限继续访问一亩地分类。
 * 后端已返回一亩地分类时使用真实菜单，避免重复显示或覆盖独立权限配置。
 */
function withLandCategoryMenu(menus) {
  const dictionary = menus.find(menu => menu && menu.name === 'dataDictionary');
  if (
    !dictionary ||
    dictionary.id === null ||
    dictionary.id === undefined ||
    menus.some(menu => menu && menu.name === 'landCategories')
  ) {
    return menus;
  }

  return menus.concat({
    id: `${dictionary.id}:landCategories`,
    parentId: dictionary.id,
    name: 'landCategories',
    hidden: dictionary.hidden,
    sort: 0,
  });
}

/**
 * 将接口返回的菜单排序值统一转换为数字。
 * 后端有时会把排序值序列化成字符串，统一处理可以避免字符串比较导致的顺序错误。
 */
function normalizeSort(value, defaultValue = 0) {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  const sort = Number(value);
  return Number.isFinite(sort) ? sort : defaultValue;
}

/** 根据菜单名称建立索引，避免在递归过滤时反复遍历整个菜单数组。 */
function createMenuMap(menus) {
  const menuMap = Object.create(null);
  (menus || []).forEach(menu => {
    if (menu && menu.name) {
      menuMap[menu.name] = menu;
    }
  });
  return menuMap;
}

/** 计算路由在浏览器中的完整地址，支持绝对路径和相对路径。 */
function resolvePath(parentPath, routePath) {
  const path = routePath || '';
  if (path.charAt(0) === '/') {
    return path;
  }
  if (!parentPath || parentPath === '/') {
    return `/${path}`.replace(/\/$/, '') || '/';
  }
  return `${parentPath.replace(/\/$/, '')}/${path}`.replace(/\/$/, '') || '/';
}

/**
 * 收集静态路由的名称、配置和完整地址。
 * 接口只负责菜单关系，页面组件仍由前端路由表提供，因此两者需要通过 name 关联。
 */
function createRouteMap(
  routes,
  parentPath = '',
  routeMap = Object.create(null),
) {
  (routes || []).forEach(route => {
    const fullPath = resolvePath(parentPath, route.path);
    if (route.name) {
      routeMap[route.name] = { route, fullPath };
    }
    if (route.children && route.children.length > 0) {
      createRouteMap(route.children, fullPath, routeMap);
    }
  });
  return routeMap;
}

/** 将菜单字段同步到路由副本，避免多次登录时污染静态路由配置。 */
function applyMenuToRoute(route, menu) {
  const nextRoute = {
    ...route,
    meta: { ...(route.meta || {}) },
  };

  if (menu) {
    if (menu.title !== null && menu.title !== undefined && menu.title !== '') {
      nextRoute.meta.title = formatPointText(menu.title);
    }
    if (menu.icon !== null && menu.icon !== undefined && menu.icon !== '') {
      // 旧菜单仍可能返回 statistics，兼容为已有图标，避免要求重建业务数据库。
      nextRoute.meta.icon = menu.icon === 'statistics' ? 'total-week' : menu.icon;
    }
    if (menu.hidden !== null && menu.hidden !== undefined) {
      nextRoute.hidden = normalizeSort(menu.hidden) !== 0;
    }
    nextRoute.sort = normalizeSort(menu.sort);
  }

  return nextRoute;
}

/**
 * 按权限递归复制静态路由。
 * hidden 路由即使没有出现在接口菜单中也要保留，因为列表页的新增、编辑等操作可能会跳转到它们。
 */
function filterRoute(route, menuMap) {
  // 独立编辑页沿用资料列表的菜单权限，不能因 hidden 而向无权限用户开放。
  if (route.meta && route.meta.permissionMenu && !menuMap[route.meta.permissionMenu]) {
    return null;
  }
  const menu = route.name ? menuMap[route.name] : null;
  const nextRoute = applyMenuToRoute(route, menu);
  const children = (route.children || [])
    .map(child => filterRoute(child, menuMap))
    .filter(Boolean);

  if (children.length > 0 || route.children) {
    nextRoute.children = children;
  }

  // 目录本身不授予页面权限，只有首页目录时不能保留指向仪表盘的重定向。
  if (route.children && children.length === 0) {
    return null;
  }

  if (!route.name) {
    return nextRoute;
  }

  if (menu) {
    return nextRoute;
  }

  if (route.hidden === true) {
    nextRoute.sort = -1;
    return nextRoute;
  }

  // 父菜单缺失但子菜单有权限时保留一个隐藏路由容器，确保页面地址仍然可访问。
  if (children.length > 0) {
    nextRoute.hidden = true;
    nextRoute.sort = -1;
    return nextRoute;
  }

  return null;
}

/**
 * 递归筛选并排序真实路由。
 * sort 按接口约定使用升序：数值越小越靠前；相同 sort 时保持接口返回顺序。
 */
function sortRoutes(routes) {
  (routes || []).forEach(route => {
    if (route.children && route.children.length > 0) {
      route.children = sortRoutes(route.children);
    }
  });

  return (routes || []).sort((left, right) => {
    const sortDiff = normalizeSort(left.sort) - normalizeSort(right.sort);
    return sortDiff;
  });
}

/** 为侧边栏复制一个菜单节点，并记录页面真实地址。 */
function createSidebarNode(menu, routeInfo, order) {
  const route = applyMenuToRoute(routeInfo.route, menu);
  return {
    ...route,
    fullPath: routeInfo.fullPath,
    children: [],
    _menuId: menu.id,
    _parentId: menu.parentId,
    _menuOrder: order,
  };
}

/**
 * 根据接口 parentId 建立侧边栏树。
 * 这里不依赖静态路由原本的父子关系，所以后端调整菜单层级后前端无需改路由文件。
 */
function buildSidebarRoutes(menus, routeMap) {
  const nodes = [];
  const nodeMap = Object.create(null);

  (menus || []).forEach((menu, order) => {
    const routeInfo = menu && menu.name ? routeMap[menu.name] : null;
    // 没有前端页面组件的菜单无法安全跳转，跳过它但不影响其他菜单建树。
    if (!routeInfo || menu.id === null || menu.id === undefined) {
      return;
    }
    const node = createSidebarNode(menu, routeInfo, order);
    nodes.push(node);
    nodeMap[String(menu.id)] = node;
  });

  const roots = [];
  const attached = new Set();
  const cycleNodeIds = new Set();
  const visitState = Object.create(null);
  const stack = [];
  const stackIndex = Object.create(null);

  // 用一次 DFS 标记循环节点，避免每个节点重复向上查找导致 O(n²) 复杂度。
  const visitParent = node => {
    const nodeId = String(node._menuId);
    if (visitState[nodeId] === 2) {
      return;
    }
    if (visitState[nodeId] === 1) {
      const cycleStart = stackIndex[nodeId];
      stack.slice(cycleStart).forEach(cycleId => cycleNodeIds.add(cycleId));
      return;
    }

    visitState[nodeId] = 1;
    stackIndex[nodeId] = stack.length;
    stack.push(nodeId);
    const parentId = node._parentId;
    const parent =
      parentId === null || parentId === undefined
        ? null
        : nodeMap[String(parentId)];
    if (parent) {
      visitParent(parent);
    }
    stack.pop();
    delete stackIndex[nodeId];
    visitState[nodeId] = 2;
  };

  nodes.forEach(visitParent);

  nodes.forEach(node => {
    const parentId = node._parentId;
    const parent =
      parentId === null || parentId === undefined
        ? null
        : nodeMap[String(parentId)];

    if (parent && !cycleNodeIds.has(String(node._menuId))) {
      parent.children.push(node);
      attached.add(String(node._menuId));
    }
  });

  nodes.forEach(node => {
    if (!attached.has(String(node._menuId))) {
      roots.push(node);
    }
  });

  const sortTree = tree => {
    tree.sort((left, right) => {
      const sortDiff = normalizeSort(left.sort) - normalizeSort(right.sort);
      return sortDiff || left._menuOrder - right._menuOrder;
    });
    tree.forEach(node => {
      if (node.children.length > 0) {
        sortTree(node.children);
      }
    });
    return tree;
  };

  const pruneEmptyDirectories = tree =>
    tree.filter(node => {
      node.children = pruneEmptyDirectories(node.children);
      return node.children.length > 0 || !routeMap[node.name].route.children;
    });

  return sortTree(pruneEmptyDirectories(roots));
}

/** 从可见菜单中选择实际页面，跳过目录和隐藏的业务编辑页。 */
function findFirstPage(routes, routeMap) {
  for (const route of routes) {
    if (route.hidden) {
      continue;
    }
    if (route.children.length > 0) {
      const path = findFirstPage(route.children, routeMap);
      if (path) {
        return path;
      }
    } else if (!routeMap[route.name].route.children) {
      return route.fullPath;
    }
  }
  return '';
}

const permission = {
  state: {
    routers: constantRouterMap,
    addRouters: [],
    routesLoaded: false,
    hasDashboard: false,
    landingPath: '/403',
  },
  mutations: {
    SET_ROUTERS: (
      state,
      { routes, sidebarRoutes, hasDashboard, landingPath },
    ) => {
      state.addRouters = routes;
      state.routers = constantRouterMap.concat(sidebarRoutes);
      state.routesLoaded = true;
      state.hasDashboard = hasDashboard;
      state.landingPath = landingPath;
    },
    RESET_ROUTERS: state => {
      state.addRouters = [];
      state.routers = constantRouterMap;
      state.routesLoaded = false;
      state.hasDashboard = false;
      state.landingPath = '/403';
    },
  },
  actions: {
    ResetRoutes({ commit }) {
      resetRouter();
      commit('RESET_ROUTERS');
    },
    GenerateRoutes({ commit }, data) {
      return new Promise(resolve => {
        const menus = withLandCategoryMenu(
          Array.isArray(data && data.menus) ? data.menus : [],
        );
        const menuMap = createMenuMap(menus);
        const routeMap = createRouteMap(asyncRouterMap);
        const accessedRouters = asyncRouterMap
          .map(route => filterRoute(route, menuMap))
          .filter(Boolean);
        const sidebarRoutes = buildSidebarRoutes(menus, routeMap);
        const hasDashboard = Boolean(menuMap.dashboard);
        const landingPath = hasDashboard
          ? routeMap.dashboard.fullPath
          : findFirstPage(sidebarRoutes, routeMap) || '/403';

        sortRoutes(accessedRouters);
        commit('SET_ROUTERS', {
          routes: accessedRouters,
          sidebarRoutes,
          hasDashboard,
          landingPath,
        });
        resolve();
      });
    },
  },
};

export default permission;



// WEBPACK FOOTER //
// ./src/store/modules/permission.js